"use client";

import {Stripe, StripeContent} from "~/app/components/stripe/stripe";
import {Box, Typography} from "@mui/material";
import {PressListItem} from "~/app/components/press/PressPost";
import {PressFilterBar} from "~/app/components/press/PressFilterBar";
import {useSearchParams} from "next/navigation";
import {PaginationBar} from "~/app/components/pagination/PaginationBar";
import {PressPost} from "~/features/press/model";
import {PAGE_SIZE, paginate} from "~/app/components/pagination/pagination";

type Props = {
  posts: PressPost[];
};

function filterPosts(posts: PressPost[], searchParams: URLSearchParams) {
  const source = searchParams.get("source");
  const category = searchParams.get("category");

  return posts.filter(post => {
    if (source && post.source !== source) return false;
    if (category && post.category !== category) return false;
    return true;
  });
}

function sortByDateDesc(posts: PressPost[]) {
  return [...posts].sort(
    (a, b) =>
      new Date(b.publishedAt).getTime() -
      new Date(a.publishedAt).getTime()
  );
}

export function PressClient({posts}: Props) {
  const searchParams = useSearchParams();
  const page = Number(searchParams.get("page") ?? "1");

  const filtered = filterPosts(posts, searchParams);
  const sorted = sortByDateDesc(filtered);

  const pageCount = Math.ceil(sorted.length / PAGE_SIZE);
  const safePage = Math.min(page, pageCount || 1);
  const paginated = paginate(sorted, safePage);

  return (
    <>
      <Stripe sx={{mb: 4}}>
        <StripeContent>
          <PressFilterBar/>
        </StripeContent>

        {paginated.map(post => (
          <PressListItem key={post.id} post={post}/>
        ))}

        {sorted.length === 0 && (
          <StripeContent>
            <Box
              sx={{
                py: 6,
                color: "text.secondary"
              }}
            >
              Keine Einträge gefunden.
            </Box>
          </StripeContent>
        )}

        <PaginationBar
          route="/press"
          page={safePage}
          pageCount={pageCount}
          searchParams={Object.fromEntries(searchParams.entries())}
        />
      </Stripe>
    </>
  );
}
